import type { PropsWithChildren, ReactNode } from "react";
import { forwardRef, useMemo } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { SupportingText } from "@jti/core-components";

import { InputLabel } from "./InputLabel";

import styles from "./InputWrapper.module.scss";

export type InputWrapperProps = {
  htmlFor?: string;
  id?: string;
  labelId?: string;
  descriptionId?: string;
  theme?: "dark" | "light";
  helperText?: string;
  isError?: boolean;
  isSuccess?: boolean;
  isOptional?: boolean;
  isHelperTextVisible?: boolean;
  dataTestId?: string;
  label?: string;
  successText?: string;
  errorText?: ReactNode;
  containerClassName?: string;
  wrapperClassName?: string;
  showRequiredAsterisk?: boolean;
};

const themeClass = {
  dark: "dark-theme",
  light: "light-theme",
} as const;

export const InputWrapper = forwardRef<HTMLDivElement, PropsWithChildren<InputWrapperProps>>(
  (
    {
      dataTestId = "textInput",
      htmlFor,
      id,
      theme,
      label,
      helperText,
      isHelperTextVisible = false,
      isError,
      errorText,
      isSuccess,
      successText,
      isOptional,
      containerClassName,
      wrapperClassName,
      showRequiredAsterisk,
      labelId,
      descriptionId,
      children,
    },
    ref
  ) => {
    const { __ } = useTranslations();

    const displayedText = useMemo(() => {
      if (isSuccess && successText) {
        return successText;
      }
      if (isError && errorText) {
        return errorText;
      }
      return helperText;
    }, [errorText, helperText, isError, isSuccess, successText]);

    const optionalLabelText = isOptional && !showRequiredAsterisk ? __("form_fields.optional") : "";
    const displayedLabel = optionalLabelText ? `${label} ${optionalLabelText}` : label;

    const isInfoSectionVisible = Boolean(isHelperTextVisible && displayedText);

    return (
      <div
        ref={ref}
        className={classNames(styles.container, theme ? themeClass[theme] : undefined, containerClassName)}
        data-testid={dataTestId}
      >
        {label && (
          <InputLabel htmlFor={htmlFor} id={id} labelId={labelId}>
            {displayedLabel}
            {showRequiredAsterisk && <span aria-hidden="true"> *</span>}
          </InputLabel>
        )}
        <div
          data-testid="textInputWrapper"
          className={classNames(styles.wrapper, wrapperClassName, {
            [styles.wrapperError]: isError,
            [styles.wrapperSuccess]: isSuccess,
          })}
        >
          {children}
        </div>

        {isInfoSectionVisible && (
          <SupportingText
            id={descriptionId}
            variant={isError ? "error" : isSuccess ? "success" : "info"}
            data-testid="textInputInfoIcon"
          >
            {displayedText}
          </SupportingText>
        )}
      </div>
    );
  }
);
